import { useQuery } from '@zg-rentals/react-query5';
import {
  AnalyticsBucketType,
  type AnalyticsLastUpdatedResponseDto,
  AnalyticsSearchType,
  ClientAdProductType,
  type CustomerAnalyticsListingFilterType,
  type CustomerAnalyticsSpecialityHousingType,
  type ListingAlias,
  type ListingQualityIssuesDto,
  MarketAnalyticsWebApiHandler_getListingQualityScoreForAliases,
  PortalAnalyticsWebApiHandler_getAnalyticsLastUpdatedDates,
  PortalAnalyticsWebApiHandler_getAnalyticsLastUpdatedDates_Path,
  PortalAnalyticsWebApiHandler_getPortfolioAnalytics,
  PortalAnalyticsWebApiHandler_getPortfolioAnalytics_Path,
  type PortfolioAnalyticsCustomerDto,
  type PortfolioAnalyticsPropertyResponseDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { addMonths, startOfMonth, startOfQuarter, subQuarters, subYears } from 'date-fns';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  applyFilters,
  type getCitiesOptions,
  type getCustomersOptions,
  type getMetropolitanServiceAreasOptions,
  type getStatesOptions,
  type getZIPCodesOptions,
  useFiltersCallbacks,
  type USStateKey,
} from './MainPageLayout.utils';
import { ALL_US_STATES_AND_TERRITORIES_MAP } from '@zg-rentals/states-territories';
import { DisplayingRegionLevel } from '../../../types/DisplayingRegionLevel';
import { useGeojsonToDisplay } from './useGeoJsonToDisplay';
import type { GeoJson } from '../../SharedComponents/Map/GeoJsonLayer';
import React from 'react';
import { US_REGION_ID } from '../../../constants';

export interface MapCenter {
  eastWestLng: number;
  northSouthLat: number;
}

export interface MapBounds {
  eastLng: number;
  northLat: number;
  southLat: number;
  westLng: number;
}

export enum MainLayoutType {
  REPORT = 'report',
  MAP = 'map',
}

export interface MainLayoutContextValue {
  layout: MainLayoutType;
  setLayout: React.Dispatch<React.SetStateAction<MainLayoutType>>;
  isFetchingInitialData: boolean;
  allProperties: Array<PortfolioAnalyticsPropertyResponseDto>;
  customers: Array<PortfolioAnalyticsCustomerDto>;
  initialDataError: Error | null;
  selectedFilters: {
    package: ClientAdProductType | CustomerAnalyticsListingFilterType | '';
    stateRegionId: number;
    metropolitanServiceAreaRegionId: number;
    cityRegionId: number;
    zipCodeRegionId: number;
    listingAlias: ListingAlias | '';
    customerId: string;
    specialityHousingType: CustomerAnalyticsSpecialityHousingType | '';
  };
  setSelectedFilters: React.Dispatch<React.SetStateAction<MainLayoutContextValue['selectedFilters']>>;
  filteredProperties: Array<PortfolioAnalyticsPropertyResponseDto>;
  regionsMapping: Record<number, string>;
  selectedRegionId: number;
  selectedDisplayingRegionLevel: DisplayingRegionLevel;
  zoom: number;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
  center: MapCenter;
  setCenter: React.Dispatch<React.SetStateAction<MapCenter>>;
  bounds: MapBounds;
  setBounds: (bounds: MapBounds) => void;
  visibleRegionIds: Array<{ regionId: number; regionIdType: DisplayingRegionLevel }>;
  regionIdCenterMapping: RegionIdCenterMapping;
  displayMode: DisplayingRegionLevel;
  geojson: GeoJson;
  isLoadingGeojson: boolean;
  errorGeojson: Error | null;
  listingQualityScores: Record<ListingAlias, ListingQualityIssuesDto>;
  onSelectState: (state: number) => void;
  onSelectMetropolitanServiceArea: (metropolitanServiceAreaRegionId: number, stateRegionId: number) => void;
  onSelectCity: (cityRegionId: number, metropolitanServiceAreaRegionId: number, stateRegionId: number) => void;
  onSelectZIPCode: (
    zipCodeRegionId: number,
    cityRegionId: number,
    metropolitanServiceAreaRegionId: number,
    stateRegionId: number,
  ) => void;
  onSelectProperty: (listingAlias: ListingAlias | '', properties: Array<PortfolioAnalyticsPropertyResponseDto>) => void;
  onSelectPackage: (pkg: ClientAdProductType | CustomerAnalyticsListingFilterType | '') => void;
  onSelectCustomer: (customerId: string) => void;
  onSelectSpecialityHousingType: (specialityHousingType: CustomerAnalyticsSpecialityHousingType | '') => void;
  shouldForceBoundsToGeojsonBounds: boolean;
  setShouldForceBoundsToGeojsonBounds: (shouldForceBoundsToGeojsonBounds: boolean) => void;
  analyticsLastUpdatedDates: AnalyticsLastUpdatedResponseDto | undefined;
  selectedMarketId: number;
  setSelectedMarketId: React.Dispatch<React.SetStateAction<number>>;
}

export type StateOption = ReturnType<typeof getStatesOptions>;
export type MetropolitanServiceAreaOption = ReturnType<typeof getMetropolitanServiceAreasOptions>;
export type CityOption = ReturnType<typeof getCitiesOptions>;
export type ZIPCodeOption = ReturnType<typeof getZIPCodesOptions>;
export type CustomerOption = ReturnType<typeof getCustomersOptions>;

const MainLayoutContext = createContext<MainLayoutContextValue | undefined>(undefined);

const today = new Date();
const oneYearAgo = startOfMonth(addMonths(subYears(today, 1), 1));
const oneYearAndOneQuarterAgo = startOfQuarter(subQuarters(oneYearAgo, 1));

const defaultCenter: MapCenter = {
  eastWestLng: -112.411109375,
  northSouthLat: 52.14968103940025,
};

const defaultBounds: MapBounds = {
  northLat: 71.44956689215951,
  southLat: 18.504632033858456,
  eastLng: -61.34665624999999,
  westLng: -163.4755625,
};

const defaultZoom = 4;

interface RegionIdCenterMappingValue {
  center: { lat: number; lng: number };
  propertiesCount: number;
}

export type RegionIdCenterMapping = Map<number, RegionIdCenterMappingValue>;

const getDisplayMode = (zoom: number) => {
  switch (true) {
    case zoom <= 5:
      return DisplayingRegionLevel.COUNTRY;
    case zoom <= 8:
      return DisplayingRegionLevel.STATE;
    case zoom <= 9:
      return DisplayingRegionLevel.METRO_AREA;
    case zoom <= 10:
      return DisplayingRegionLevel.CITY;
    default:
      return DisplayingRegionLevel.ZIP;
  }
};

const getSelectedRegionId = (
  selectedFilters: MainLayoutContextValue['selectedFilters'],
  properties: Array<PortfolioAnalyticsPropertyResponseDto>,
): { selectedRegionId: number; regionIdType: DisplayingRegionLevel } => {
  if (selectedFilters.listingAlias) {
    const property = properties.find(
      (property) => property.propertyDetails.listingAlias === selectedFilters.listingAlias,
    );
    if (property) {
      if (property.propertyDetails.zipRegionId) {
        return {
          selectedRegionId: Number(property.propertyDetails.zipRegionId),
          regionIdType: DisplayingRegionLevel.ZIP,
        };
      }
      if (property.propertyDetails.cityRegionId) {
        return {
          selectedRegionId: Number(property.propertyDetails.cityRegionId),
          regionIdType: DisplayingRegionLevel.CITY,
        };
      }
      if (property.propertyDetails.metroStatRegionId) {
        return {
          selectedRegionId: Number(property.propertyDetails.metroStatRegionId),
          regionIdType: DisplayingRegionLevel.METRO_AREA,
        };
      }
      if (property.propertyDetails.stateRegionId) {
        return {
          selectedRegionId: Number(property.propertyDetails.stateRegionId),
          regionIdType: DisplayingRegionLevel.STATE,
        };
      }

      return { selectedRegionId: 0, regionIdType: DisplayingRegionLevel.NONE };
    }
  }

  if (selectedFilters.zipCodeRegionId) {
    return { selectedRegionId: Number(selectedFilters.zipCodeRegionId), regionIdType: DisplayingRegionLevel.ZIP };
  }
  if (selectedFilters.cityRegionId) {
    return { selectedRegionId: Number(selectedFilters.cityRegionId), regionIdType: DisplayingRegionLevel.CITY };
  }
  if (selectedFilters.metropolitanServiceAreaRegionId) {
    return {
      selectedRegionId: Number(selectedFilters.metropolitanServiceAreaRegionId),
      regionIdType: DisplayingRegionLevel.METRO_AREA,
    };
  }
  if (selectedFilters.stateRegionId) {
    return { selectedRegionId: Number(selectedFilters.stateRegionId), regionIdType: DisplayingRegionLevel.STATE };
  }

  return { selectedRegionId: 0, regionIdType: DisplayingRegionLevel.NONE };
};

export const MainLayoutProvider = ({ children }: { children: React.ReactNode }) => {
  const [layout, setLayout] = useState<MainLayoutType>(MainLayoutType.REPORT);
  const [regionsMapping, setRegionsMapping] = useState<Record<number, string> | null>(null);
  const [selectedMarketId, setSelectedMarketId] = useState<number>(US_REGION_ID);
  const [selectedFilters, setSelectedFilters] = useState<MainLayoutContextValue['selectedFilters']>({
    package: '',
    stateRegionId: 0,
    metropolitanServiceAreaRegionId: 0,
    cityRegionId: 0,
    zipCodeRegionId: 0,
    listingAlias: '',
    customerId: '',
    specialityHousingType: '',
  });
  const [zoom, setZoom] = useState(defaultZoom);
  const [center, setCenter] = useState<MapCenter>(defaultCenter);
  const [bounds, setBounds] = useState<MapBounds>(defaultBounds);
  const [regionIdCenterMapping, setRegionIdCenterMapping] = useState<RegionIdCenterMapping | null>(null);
  const [shouldForceBoundsToGeojsonBounds, setShouldForceBoundsToGeojsonBounds] = useState(false);
  const [listingQualityScores, setListingQualityScores] = useState<Record<ListingAlias, ListingQualityIssuesDto>>({});

  const queryParams = {
    bucketTypes: [AnalyticsBucketType.MONTHLY, AnalyticsBucketType.CONSOLIDATED],
    startDate: {
      year: oneYearAndOneQuarterAgo.getFullYear(),
      month: oneYearAndOneQuarterAgo.getMonth() + 1,
      day: oneYearAndOneQuarterAgo.getDate(),
    },
    endDate: { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() },
    searchType: AnalyticsSearchType.ZUID,
  };

  const {
    data: rawData,
    error,
    isLoading,
  } = useQuery({
    queryKey: [PortalAnalyticsWebApiHandler_getPortfolioAnalytics_Path, queryParams],
    queryFn: () => PortalAnalyticsWebApiHandler_getPortfolioAnalytics(queryParams),
  });

  const data = useMemo(() => {
    if (!rawData) return undefined;

    const newData = {
      ...rawData,
      properties: rawData.properties.map((property) => {
        const isSingleFamily = property.aggregateStats.isSingleFamily;
        let newProperty = property;

        if (isSingleFamily) {
          newProperty = {
            ...property,
            propertyDetails: {
              ...property.propertyDetails,
              listingName: `${property.propertyDetails.street}, ${property.propertyDetails.city}, ${property.propertyDetails.state}`,
            },
          };
        }

        if (newProperty.aggregateStats.totalDaysSpotlighted > 0) {
          newProperty = {
            ...newProperty,
            aggregateStats: {
              ...newProperty.aggregateStats,
              adproductTypes: [...newProperty.aggregateStats.adproductTypes, ClientAdProductType.LISTING_SPOTLIGHT],
            },
            breakdownStats: newProperty.breakdownStats.map((bdStat) => {
              if (bdStat.totalDaysSpotlighted > 0) {
                return {
                  ...bdStat,
                  adproductTypes: [...bdStat.adproductTypes, ClientAdProductType.LISTING_SPOTLIGHT],
                };
              }

              return bdStat;
            }),
          };
        }

        return newProperty;
      }),
    };

    return newData;
  }, [rawData]);

  const allProperties = useMemo(
    () =>
      (data?.properties ?? []).filter((property) => {
        if (layout === MainLayoutType.MAP) {
          return !property.aggregateStats.isSingleFamily;
        }

        if (selectedFilters.customerId) {
          return property.aggregateStats.customerId === selectedFilters.customerId;
        }

        return true;
      }),
    [data?.properties, layout, selectedFilters.customerId],
  );

  const filteredProperties = useMemo(
    () => applyFilters(selectedFilters, allProperties),
    [selectedFilters, allProperties],
  );

  const { data: analyticsLastUpdatedDates } = useQuery({
    queryKey: [PortalAnalyticsWebApiHandler_getAnalyticsLastUpdatedDates_Path],
    queryFn: () => PortalAnalyticsWebApiHandler_getAnalyticsLastUpdatedDates(),
  });

  const { selectedRegionId, regionIdType: selectedDisplayingRegionLevel } = getSelectedRegionId(
    selectedFilters,
    data?.properties ?? [],
  );

  React.useEffect(() => {
    if (!data) return;
    const listingAliases = data.properties
      .filter((property) => !property.aggregateStats.isSingleFamily)
      .map((property) => {
        return property.aggregateStats.currentListingAlias;
      });

    const BATCH_SIZE = 100;
    const batches: Array<Array<ListingAlias>> = [];
    for (let i = 0; i < listingAliases.length; i += BATCH_SIZE) {
      batches.push(listingAliases.slice(i, i + BATCH_SIZE));
    }

    void Promise.all(batches.map((batch) => MarketAnalyticsWebApiHandler_getListingQualityScoreForAliases(batch)))
      .then((results) => {
        const mergedResults = results.reduce(
          (acc, result) => {
            return { ...acc, ...result };
          },
          {} as Record<ListingAlias, ListingQualityIssuesDto>,
        );
        setListingQualityScores(mergedResults);
        return;
      })
      .catch(() => {
        // Error fetching listing quality scores
        // todo ask design for an error state
      });
  }, [data]);

  const displayMode = getDisplayMode(zoom);

  const {
    geojson,
    visibleRegions: visibleRegionIds,
    isLoading: isLoadingGeojson,
    error: errorGeojson,
  } = useGeojsonToDisplay(
    selectedRegionId,
    selectedDisplayingRegionLevel,
    filteredProperties,
    displayMode,
    bounds,
    setBounds,
    regionIdCenterMapping,
    layout,
    shouldForceBoundsToGeojsonBounds,
    setShouldForceBoundsToGeojsonBounds,
    selectedFilters,
  );

  const {
    onSelectState,
    onSelectMetropolitanServiceArea,
    onSelectCity,
    onSelectZIPCode,
    onSelectProperty,
    onSelectPackage,
    onSelectCustomer,
    onSelectSpecialityHousingType,
  } = useFiltersCallbacks(selectedFilters, setSelectedFilters, setShouldForceBoundsToGeojsonBounds);

  useEffect(() => {
    if (allProperties.length > 0 && data && !error && !isLoading) {
      if (!regionsMapping) {
        const mapping: Record<number, string> = data.properties.reduce(
          (acc, property) => {
            const state = property.propertyDetails.state as USStateKey;
            if (state) {
              acc[property.propertyDetails.stateRegionId] = ALL_US_STATES_AND_TERRITORIES_MAP[state] ?? state;
            }

            if (property.propertyDetails.metroArea) {
              acc[property.propertyDetails.metroStatRegionId] = property.propertyDetails.metroArea.replace(
                'Metro Area',
                'Metro area',
              );
            }

            if (property.propertyDetails.city) {
              acc[property.propertyDetails.cityRegionId] = property.propertyDetails.city;
            }
            if (property.propertyDetails.zip) {
              acc[property.propertyDetails.zipRegionId] = property.propertyDetails.zip;
            }

            return acc;
          },
          {} as Record<number, string>,
        );

        setRegionsMapping(mapping);
      }

      if (!regionIdCenterMapping) {
        const mapping = data.properties.reduce((acc: RegionIdCenterMapping, property) => {
          if (property.aggregateStats.isSingleFamily) {
            return acc;
          }

          const regionIds = [
            property.propertyDetails.stateRegionId,
            property.propertyDetails.metroStatRegionId,
            property.propertyDetails.cityRegionId,
            property.propertyDetails.zipRegionId,
          ];

          regionIds.forEach((regionId) => {
            if (acc.has(regionId)) {
              acc.get(regionId)!.propertiesCount++;
              acc.get(regionId)!.center.lat += property.propertyDetails.lat;
              acc.get(regionId)!.center.lng += property.propertyDetails.lon;
            } else {
              acc.set(regionId, {
                center: { lat: property.propertyDetails.lat, lng: property.propertyDetails.lon },
                propertiesCount: 1,
              });
            }
          });

          return acc;
        }, new Map());

        mapping.forEach((value: RegionIdCenterMappingValue) => {
          value.center.lat = value.center.lat / value.propertiesCount;
          value.center.lng = value.center.lng / value.propertiesCount;
        });

        setRegionIdCenterMapping(mapping);
      }
    }
  }, [
    data,
    allProperties,
    error,
    isLoading,
    setRegionsMapping,
    regionsMapping,
    setRegionIdCenterMapping,
    regionIdCenterMapping,
  ]);

  return (
    <MainLayoutContext.Provider
      value={{
        layout,
        setLayout,
        isFetchingInitialData: isLoading,
        allProperties,
        customers: data?.customers ?? [],
        initialDataError: error,
        selectedFilters,
        setSelectedFilters,
        filteredProperties,
        regionsMapping: regionsMapping ?? {},
        selectedRegionId,
        selectedDisplayingRegionLevel,
        zoom,
        setZoom,
        center,
        setCenter,
        bounds,
        setBounds,
        visibleRegionIds,
        regionIdCenterMapping: regionIdCenterMapping ?? new Map(),
        displayMode,
        geojson,
        isLoadingGeojson,
        errorGeojson,
        listingQualityScores,
        onSelectState,
        onSelectMetropolitanServiceArea,
        onSelectCity,
        onSelectZIPCode,
        onSelectProperty,
        onSelectPackage,
        onSelectCustomer,
        onSelectSpecialityHousingType,
        shouldForceBoundsToGeojsonBounds,
        setShouldForceBoundsToGeojsonBounds,
        analyticsLastUpdatedDates,
        selectedMarketId,
        setSelectedMarketId,
      }}
    >
      {children}
    </MainLayoutContext.Provider>
  );
};

export const useMainLayout = () => {
  const context = useContext(MainLayoutContext);
  if (!context) {
    throw new Error('useMainLayout must be used within a MainLayoutProvider');
  }

  return context;
};
